import { useCallback, type FC } from "react";
import { useMutation } from "@apollo/client";
import { Button, Tooltip } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { useSnackbar } from "notistack";
import { useTranslation } from "@/shared/hooks";
import { useUser } from "@/shared/hooks/use-user/useUser";
import {
  ContentSubscriptionFieldsFragment,
  UpdateContentSubscriptionMutation,
} from "@/entities/contents";
import type {
  UpdateContentSubscriptionMutationType,
  UpdateContentSubscriptionMutationVariablesType,
} from "@/types/graphql/site.generated";

export interface StreamReminderButtonProps {
  contentId: string;
  viewerSubscription: { id: string; active: boolean } | null;
}

/**
 * Колокольчик «Напомнить о начале» на экране ожидающего стрима (порт
 * референсного SubscribeStreamButton). Гостю клик открывает диалог входа тем
 * же способом, что и шапка витрины: через useUser().openSignIn.
 */
export const StreamReminderButton: FC<StreamReminderButtonProps> = ({
  contentId,
  viewerSubscription,
}) => {
  const { t } = useTranslation();
  const { user, openSignIn } = useUser();
  const { enqueueSnackbar } = useSnackbar();

  const [updateContentSubscription, { data, loading }] = useMutation<
    UpdateContentSubscriptionMutationType,
    UpdateContentSubscriptionMutationVariablesType
  >(UpdateContentSubscriptionMutation, {
    onError: () => {
      enqueueSnackbar(t("stream.reminder.error"), { variant: "error" });
    },
    update(cache, result) {
      const subscription = result.data?.updateContentSubscription;
      if (!subscription) return;
      // Апollo нормализует ContentSubscription по id, но не знает, что
      // Content.viewerSubscription (раньше null) должен теперь на него
      // указывать — дописываем ссылку вручную, иначе экран стрима остался бы
      // со старым значением до следующего запроса с сервера.
      cache.modify({
        id: cache.identify({ __typename: "Content", id: contentId }),
        fields: {
          viewerSubscription: () =>
            cache.writeFragment({
              data: subscription,
              fragment: ContentSubscriptionFieldsFragment,
            }),
        },
      });
    },
  });

  // Ответ мутации приоритетнее пропа (как в референсе): даёт мгновенную
  // обратную связь, даже если запись в кэш по какой-то причине не сработала.
  const subscription = data?.updateContentSubscription ?? viewerSubscription;
  const active = Boolean(subscription?.active);

  const onClick = useCallback(() => {
    if (!user) {
      openSignIn();
      return;
    }

    void updateContentSubscription({
      variables: { contentId, active: !active },
    }).then((result) => {
      const next = result.data?.updateContentSubscription;
      if (!next) return undefined;
      enqueueSnackbar(
        next.active
          ? t("stream.reminder.snackbarOn", { email: user.email })
          : t("stream.reminder.snackbarOff"),
        { variant: "success", preventDuplicate: true },
      );
      return undefined;
    });
  }, [
    active,
    contentId,
    enqueueSnackbar,
    openSignIn,
    t,
    updateContentSubscription,
    user,
  ]);

  return (
    <Tooltip
      title={
        active
          ? t("stream.reminder.tooltipOff")
          : t("stream.reminder.tooltipOn")
      }
    >
      {/* span нужен, чтобы Tooltip работал и на disabled-кнопке */}
      <span>
        <Button
          variant={active ? "contained" : "outlined"}
          color={active ? "primary" : "inherit"}
          disabled={loading}
          onClick={onClick}
          startIcon={
            active ? <NotificationsActiveIcon /> : <NotificationsIcon />
          }
          sx={{ whiteSpace: "nowrap" }}
        >
          {active
            ? t("stream.reminder.reminderOn")
            : t("stream.reminder.remindMe")}
        </Button>
      </span>
    </Tooltip>
  );
};
